/** Tag loading decisions. Missing or malformed ids never load a tag. */

import type { StoredConsent } from './consent';

/** Confirmed ReHub Studio property. Env can override; these are the platform defaults. */
export const REHUB_GA4_MEASUREMENT_ID = 'G-9QTFLVVETE';
/** Confirmed Google Ads account. Configured once on the same gtag.js as GA4. */
export const REHUB_GOOGLE_ADS_ID = 'AW-18051226602';
/**
 * Website registration action in account 395-796-7091.
 * Fired only after a new auth user is claimed, with no value and no personal data.
 */
export const REHUB_GOOGLE_ADS_SIGNUP_SEND_TO = 'AW-18051226602/5SmoCKWF6p0dEOq3v59D';
/**
 * Website purchase action in account 395-796-7091.
 * The event sends the verified Checkout amount, currency, and transaction id.
 * Google Ads must be set to "Use different values for each conversion".
 */
export const REHUB_GOOGLE_ADS_PURCHASE_SEND_TO = 'AW-18051226602/vmeVCNjx1bccEOq3v59D';
/** Confirmed ReHub Studio Main dataset. */
export const REHUB_META_PIXEL_ID = '1096120589566928';

/**
 * Registrable domains for Google's linker. Subdomains are included by the tag.
 * photoedit.studio is the Ads landing host. rehub.studio is where registration
 * and payment are completed. Do not invent extra query parameters.
 */
export const GOOGLE_LINKER_DOMAINS = ['photoedit.studio', 'rehub.studio'] as const;

const GA4_ID = /^G-[A-Z0-9]+$/;
const META_PIXEL_ID = /^\d{8,20}$/;
const GOOGLE_ADS_ID = /^AW-\d+$/;
const GOOGLE_ADS_SEND_TO = /^AW-\d+\/[A-Za-z0-9_-]+$/;
const LINKEDIN_PARTNER_ID = /^\d{5,12}$/;
const LINKEDIN_CONVERSION_ID = /^\d{5,12}$/;

export interface TagConfig {
  ga4MeasurementId?: string;
  googleAdsId?: string;
  metaPixelId?: string;
  googleAdsSignupSendTo?: string;
  googleAdsFirstToolSendTo?: string;
  googleAdsPurchaseSendTo?: string;
  linkedInPartnerId?: string;
  linkedInSignupConversionId?: string;
  linkedInFirstToolConversionId?: string;
  linkedInPurchaseConversionId?: string;
  debugMode?: boolean;
  metaTestEventCode?: string;
}

export interface InitialisedTags {
  ga4: string[];
  googleAds: string[];
  meta: string[];
  linkedin: string[];
}

function cleanId(raw: string | undefined, pattern: RegExp): string | undefined {
  const value = raw?.trim();
  if (!value || !pattern.test(value)) return undefined;
  return value;
}

/** A same-account override wins. An empty or foreign-account value keeps the verified label. */
function adsDestination(raw: string | undefined, fallback: string, adsId: string): string | undefined {
  const cleaned = cleanId(raw, GOOGLE_ADS_SEND_TO);
  if (cleaned?.startsWith(`${adsId}/`)) return cleaned;
  if (fallback.startsWith(`${adsId}/`)) return fallback;
  return undefined;
}

export function tagConfigFromEnv(env: Record<string, string | undefined>): TagConfig {
  const testCode = (env.VITE_META_TEST_EVENT_CODE || env.NEXT_PUBLIC_META_TEST_EVENT_CODE || '').trim();
  const googleAdsId = cleanId(env.VITE_GOOGLE_ADS_ID || env.NEXT_PUBLIC_GOOGLE_ADS_ID, GOOGLE_ADS_ID) ?? REHUB_GOOGLE_ADS_ID;
  return {
    ga4MeasurementId: cleanId(env.VITE_GA4_MEASUREMENT_ID || env.NEXT_PUBLIC_GA4_MEASUREMENT_ID, GA4_ID) ?? REHUB_GA4_MEASUREMENT_ID,
    googleAdsId,
    metaPixelId: cleanId(env.VITE_META_PIXEL_ID || env.NEXT_PUBLIC_META_PIXEL_ID, META_PIXEL_ID) ?? REHUB_META_PIXEL_ID,
    googleAdsSignupSendTo: adsDestination(
      env.VITE_GOOGLE_ADS_SIGNUP_SEND_TO || env.NEXT_PUBLIC_GOOGLE_ADS_SIGNUP_SEND_TO,
      REHUB_GOOGLE_ADS_SIGNUP_SEND_TO,
      googleAdsId,
    ),
    googleAdsFirstToolSendTo: cleanId(env.VITE_GOOGLE_ADS_FIRST_TOOL_SEND_TO || env.NEXT_PUBLIC_GOOGLE_ADS_FIRST_TOOL_SEND_TO, GOOGLE_ADS_SEND_TO),
    googleAdsPurchaseSendTo: adsDestination(
      env.VITE_GOOGLE_ADS_PURCHASE_SEND_TO || env.NEXT_PUBLIC_GOOGLE_ADS_PURCHASE_SEND_TO,
      REHUB_GOOGLE_ADS_PURCHASE_SEND_TO,
      googleAdsId,
    ),
    linkedInPartnerId: cleanId(env.VITE_LINKEDIN_PARTNER_ID || env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID, LINKEDIN_PARTNER_ID),
    linkedInSignupConversionId: cleanId(env.VITE_LINKEDIN_SIGNUP_CONVERSION_ID || env.NEXT_PUBLIC_LINKEDIN_SIGNUP_CONVERSION_ID, LINKEDIN_CONVERSION_ID),
    linkedInFirstToolConversionId: cleanId(env.VITE_LINKEDIN_FIRST_TOOL_CONVERSION_ID || env.NEXT_PUBLIC_LINKEDIN_FIRST_TOOL_CONVERSION_ID, LINKEDIN_CONVERSION_ID),
    linkedInPurchaseConversionId: cleanId(env.VITE_LINKEDIN_PURCHASE_CONVERSION_ID || env.NEXT_PUBLIC_LINKEDIN_PURCHASE_CONVERSION_ID, LINKEDIN_CONVERSION_ID),
    debugMode: trackingDebugEnabled(env),
    metaTestEventCode: testCode || undefined,
  };
}

export function adsSendTo(config: TagConfig, slot: 'signup' | 'first_tool' | 'purchase'): string | undefined {
  const label = slot === 'signup'
    ? config.googleAdsSignupSendTo
    : slot === 'first_tool'
      ? config.googleAdsFirstToolSendTo
      : config.googleAdsPurchaseSendTo;
  if (!label || !config.googleAdsId || !label.startsWith(`${config.googleAdsId}/`)) return undefined;
  return label;
}

export function linkedInConversionId(config: TagConfig, slot: 'signup' | 'first_tool' | 'purchase'): string | undefined {
  if (slot === 'signup') return config.linkedInSignupConversionId;
  if (slot === 'first_tool') return config.linkedInFirstToolConversionId;
  return config.linkedInPurchaseConversionId;
}

export interface TagLoadPlan {
  ga4MeasurementId?: string;
  googleAdsId?: string;
  metaPixelId?: string;
  linkedInPartnerId?: string;
}

/**
 * GA4 loads only with analytics consent.
 * Google Ads, Meta, and LinkedIn load only with advertising consent.
 * An id that is already initialised is omitted so the page does not install it twice.
 * Google Ads uses the same gtag.js as GA4; the plan only names the destination.
 */
export function planTagLoads(
  config: TagConfig,
  consent: StoredConsent | null,
  already: InitialisedTags = { ga4: [], googleAds: [], meta: [], linkedin: [] },
): TagLoadPlan {
  const plan: TagLoadPlan = {};
  if (consent?.analytics && config.ga4MeasurementId && !already.ga4.includes(config.ga4MeasurementId)) {
    plan.ga4MeasurementId = config.ga4MeasurementId;
  }
  if (consent?.advertising && config.googleAdsId && !already.googleAds.includes(config.googleAdsId)) {
    plan.googleAdsId = config.googleAdsId;
  }
  if (consent?.advertising && config.metaPixelId && !already.meta.includes(config.metaPixelId)) {
    plan.metaPixelId = config.metaPixelId;
  }
  if (consent?.advertising && config.linkedInPartnerId && !already.linkedin.includes(config.linkedInPartnerId)) {
    plan.linkedInPartnerId = config.linkedInPartnerId;
  }
  return plan;
}

export function trackingCutoverEnabled(env: Record<string, string | undefined>): boolean {
  return env.VITE_REHUB_TRACKING_CUTOVER === '1' || env.NEXT_PUBLIC_REHUB_TRACKING_CUTOVER === '1';
}

export function trackingDebugEnabled(env: Record<string, string | undefined>): boolean {
  return env.VITE_REHUB_TRACKING_DEBUG === '1' || env.NEXT_PUBLIC_REHUB_TRACKING_DEBUG === '1';
}

function normalHost(hostname: string): string {
  return hostname.trim().toLowerCase().replace(/\.$/, '');
}

export function isRehubStudioHost(hostname: string): boolean {
  const host = normalHost(hostname);
  return host === 'rehub.studio' || host.endsWith('.rehub.studio');
}

/**
 * Production tags load only on rehub.studio after the approved cutover flag.
 * Localhost loads only with the debug flag. Vercel previews and brand domains never load.
 */
export function platformTagsEnabled(input: { hostname: string; env: Record<string, string | undefined> }): boolean {
  const host = normalHost(input.hostname);
  const preview = host.endsWith('.vercel.app')
    || input.env.VERCEL_ENV === 'preview'
    || input.env.VERCEL_ENV === 'development'
    || input.env.NEXT_PUBLIC_VERCEL_ENV === 'preview'
    || input.env.NEXT_PUBLIC_VERCEL_ENV === 'development'
    || input.env.VITE_VERCEL_ENV === 'preview'
    || input.env.VITE_VERCEL_ENV === 'development';
  if (preview) return false;
  const local = host === 'localhost' || host === '127.0.0.1' || host === '[::1]' || host === '::1';
  if (local) return trackingDebugEnabled(input.env);
  if (!isRehubStudioHost(host)) return false;
  return trackingCutoverEnabled(input.env);
}

export interface GoogleTagInstallPlan {
  /** Ask for gtag.js only when this document does not already have one. */
  loadScript: boolean;
  /** Omitted when a gtag is already on the page, so GA4 is not configured twice. */
  configureGa4?: string;
  /** Set when advertising consent allows and this Ads id is not already marked. */
  configureAds?: string;
  /** An existing gtag is reused for Ads. No second script is added. */
  reuseExistingGtag: boolean;
}

/**
 * One Google tag per document. A page that already has GA4 still receives the
 * Ads destination. It does not receive a second gtag.js or a second GA4 config.
 */
export function planGoogleTagInstall(input: {
  config: TagConfig;
  consent: StoredConsent | null;
  already: InitialisedTags;
  foreignGtag: boolean;
}): GoogleTagInstallPlan {
  const loads = planTagLoads(input.config, input.consent, input.already);
  const configureGa4 = input.foreignGtag ? undefined : loads.ga4MeasurementId;
  const configureAds = loads.googleAdsId;
  return {
    loadScript: !input.foreignGtag && Boolean(configureGa4 || configureAds),
    configureGa4,
    configureAds,
    reuseExistingGtag: input.foreignGtag && Boolean(configureAds),
  };
}

export function shouldAppendGoogleScript(input: {
  plan: GoogleTagInstallPlan;
  scriptAlreadyPresent: boolean;
}): boolean {
  return input.plan.loadScript && !input.scriptAlreadyPresent && !input.plan.reuseExistingGtag;
}

export function googleScriptSrc(measurementId: string): string {
  return `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
}

/** True when this dataLayer already contains gtag('config', id). */
export function dataLayerHasConfig(dataLayer: unknown[], id: string): boolean {
  return dataLayer.some((entry) => {
    if (typeof entry !== 'object' || !entry) return false;
    const command = entry as ArrayLike<unknown>;
    return command.length >= 2 && command[0] === 'config' && command[1] === id;
  });
}

export function googleLinkerSettings(): { domains: string[]; accept_incoming: true } {
  return { domains: [...GOOGLE_LINKER_DOMAINS], accept_incoming: true };
}

/**
 * Inline bootstrap for a landing host that already has its own GA4 property.
 * One gtag.js, then one config for that property and one for Google Ads.
 * Callers must render this only after the visitor has accepted cookies.
 * It does not send a conversion and does not set user_data.
 */
export function googleLandingBootstrap(input: { ga4MeasurementId: string; googleAdsId: string }): string {
  const linker = JSON.stringify(googleLinkerSettings());
  const denied = JSON.stringify({
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
  });
  const granted = JSON.stringify({
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
  const ga4 = JSON.stringify(input.ga4MeasurementId);
  const ads = JSON.stringify(input.googleAdsId);
  return [
    'window.dataLayer=window.dataLayer||[];',
    'function gtag(){dataLayer.push(arguments);}',
    `gtag('consent','default',${denied});`,
    `gtag('consent','update',${granted});`,
    "gtag('set','ads_data_redaction',true);",
    "gtag('set','url_passthrough',true);",
    `gtag('set','linker',${linker});`,
    'gtag(\'js\',new Date());',
    `gtag('config',${ga4},{anonymize_ip:true,linker:${linker}});`,
    `gtag('config',${ads},{send_page_view:false,linker:${linker}});`,
  ].join('');
}
